import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useRoles } from '@/hooks/useRoles';
import { useAuth } from './AuthContext';
import type { Role } from '@/lib/rolesCache';

interface CapabilitiesContextType {
  capabilities: Set<string>;
  hasCapability: (name: string) => boolean;
  isLoading: boolean;
}

const CapabilitiesContext = createContext<CapabilitiesContextType>({
  capabilities: new Set(),
  hasCapability: () => false,
  isLoading: true,
});

export const useCapabilities = () => useContext(CapabilitiesContext);

interface CapabilitiesProviderProps {
  children: React.ReactNode;
}

/** Recursively traverse the roles tree and OR-merge all true capabilities */
function extractCapabilities(roles: Role[]): Set<string> {
  const caps = new Set<string>();
  const traverse = (nodes: Role[]) => {
    for (const node of nodes) {
      if (node.capabilities) {
        for (const [key, val] of Object.entries(node.capabilities)) {
          if (val) caps.add(key);
        }
      }
      if (node.children?.length) traverse(node.children);
    }
  };
  traverse(roles);
  return caps;
}

export const CapabilitiesProvider: React.FC<CapabilitiesProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading, fetchRoles } = useRoles();

  useEffect(() => {
    if (user) {
      fetchRoles();
    }
  }, [user, fetchRoles]);

  const capabilities = useMemo(() => extractCapabilities(roles), [roles]);

  const hasCapability = useMemo(
    () => (name: string) => capabilities.has(name),
    [capabilities]
  );

  const value = useMemo(
    () => ({
      capabilities,
      hasCapability,
      isLoading: rolesLoading,
    }),
    [capabilities, hasCapability, rolesLoading]
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
};
